import { useAuthAndProfile } from "@/hooks/useAuthAndProfile";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LogOut, Settings, User, AlertTriangle, XCircle } from "lucide-react";
import { useSubscriptionManagement } from "@/hooks/useSubscriptionManagement";

export function UserMenu() {
  const { isAuthenticated, session, hasPaidAccess, accessLevel } = useAuthAndProfile();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { subscription, cancelSubscription, isLoading } = useSubscriptionManagement();

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Déconnexion réussie",
        description: "Vous avez été déconnecté avec succès.",
      });
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la déconnexion.",
        variant: "destructive",
      });
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const userEmail = session?.user?.email || "Utilisateur";
  const userInitials = userEmail.charAt(0).toUpperCase();

  // Access state is now store-driven (stores.plan + stores.trial_ends_at)
  const isTrialExpired = !hasPaidAccess && accessLevel === "blocked";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary text-primary-foreground">
              {userInitials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{userEmail}</p>
            {isTrialExpired && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Essai gratuit expiré
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/profil")}>
          <User className="mr-2 h-4 w-4" />
          <span>Profil</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/parametres")}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Paramètres</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/gestion-compte")}>
          <AlertTriangle className="mr-2 h-4 w-4" />
          <span>Gestion du compte</span>
        </DropdownMenuItem>
        {subscription?.status === 'active' && (
          <DropdownMenuItem onClick={cancelSubscription} disabled={isLoading} className="text-orange-600">
            <XCircle className="mr-2 h-4 w-4" />
            <span>{isLoading ? 'Chargement...' : 'Annuler abonnement'}</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Se déconnecter</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}